import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getHobbies } from '../../../api/hobbies/get-hobbies'
import useTranslate from '../../../lib/hooks/use-translate'
import { HobbyAudienceEnum } from '../../../lib/types/enums/hobby/hobby-audience.enum'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'

type HobbiesSelectionProps = {
  value: string[]
  onChange: (hobbies: string[]) => void
  audience: HobbyAudienceEnum
}

const HobbiesSelection = ({ value, onChange, audience }: HobbiesSelectionProps) => {
  const { t } = useTranslate()
  const [showOtherInput, setShowOtherInput] = useState(false)
  const mountRef = useRef(true)

  const { data: hobbies, isLoading } = useQuery({
    queryKey: ['hobbies', { automaticallyGenerated: false, visibleTo: [audience] }],
    queryFn: () => getHobbies({ filter: { automaticallyGenerated: false, visibleTo: [audience] } })
  })

  const mainHobbies = useMemo(() => hobbies?.data || [], [hobbies?.data])

  useEffect(() => {
    if (!hobbies || mountRef.current === false) return

    const customNames = value.filter(
      (name) => !mainHobbies.some((hobby) => hobby.name.toLowerCase() === name.toLowerCase())
    )

    setShowOtherInput(customNames.length > 0)
    mountRef.current = false
  }, [hobbies, mainHobbies, value])

  if (isLoading || !hobbies) return <Loader center />

  return (
    <InnerBox className="rounded-lg p-4 flex flex-col gap-4">
      <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
        {t('hobbies.title')}
      </Typography>

      <div className="grid grid-cols-3 gap-3 mobile:grid-cols-2">
        {mainHobbies?.map((hobby) => (
          <Checkbox
            key={hobby.id}
            label={hobby.name}
            checked={value.includes(hobby.name)}
            onChange={(checked) => {
              const updated = checked
                ? [...value, hobby.name]
                : value.filter((name) => name !== hobby.name)
              onChange(updated)
            }}
          />
        ))}

        <Checkbox
          label={t('hobbies.input.other')}
          checked={showOtherInput}
          onChange={(checked) => {
            setShowOtherInput(checked)

            if (!checked) {
              // Remove all custom hobbies when "Autre" is unchecked
              const cleaned = value.filter((name) =>
                mainHobbies.some((hobby) => hobby.name.toLowerCase() === name.toLowerCase())
              )
              onChange(cleaned)
            }
          }}
        />
      </div>

      {showOtherInput && (
        <ControlledMultiSelectInput<string>
          label={t('hobbies.input.title')}
          variant="primary"
          onChange={(newHobbyNames) => {
            const matchedPredefined = new Set<string>()
            const customHobbies: string[] = []

            newHobbyNames.forEach((inputName) => {
              const matched = mainHobbies.find(
                (hobby) => hobby.name.toLowerCase() === inputName.toLowerCase()
              )

              if (matched) {
                matchedPredefined.add(matched.name) // preserve correct casing
              } else {
                customHobbies.push(inputName)
              }
            })

            // ✅ Keep already checked predefined hobbies
            const currentPredefined = value.filter((name) =>
              mainHobbies.some((hobby) => hobby.name === name)
            )

            // ✅ Merge and deduplicate
            const merged = Array.from(
              new Set([...currentPredefined, ...matchedPredefined, ...customHobbies])
            )

            onChange(merged)
            setShowOtherInput(customHobbies.length > 0)
          }}
          selectedOptions={value
            .filter((name) => {
              return !mainHobbies.some((h) => h.name.toLowerCase() === name.toLowerCase())
            })
            .map((name) => ({
              label: name,
              value: name
            }))}
        />
      )}
    </InnerBox>
  )
}

export default HobbiesSelection
